import { useQueryClient } from '@tanstack/react-query';

export const useQueryInvalidation = () => {
  const queryClient = useQueryClient();

  const normalizeId = (value?: string | number) => {
    if (value === undefined || value === null) {
      return undefined;
    }
    return typeof value === 'number' ? String(value) : value;
  };

  const invalidateByKey = (key: unknown[]) => {
    queryClient.invalidateQueries({ queryKey: key });
  };

  const invalidateAll = () => {
    // Invalidar todas as queries relacionadas ao dashboard e listagens
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    queryClient.invalidateQueries({ queryKey: ['alunos'] });
    queryClient.invalidateQueries({ queryKey: ['empresas'] });
    queryClient.invalidateQueries({ queryKey: ['cidades'] });
    queryClient.invalidateQueries({ queryKey: ['cursos'] });
    queryClient.invalidateQueries({ queryKey: ['instituicoes'] });
    queryClient.invalidateQueries({ queryKey: ['seguradoras'] });
    queryClient.invalidateQueries({ queryKey: ['agentes-integradores'] });
    queryClient.invalidateQueries({ queryKey: ['estagios'] });
    queryClient.invalidateQueries({ queryKey: ['relatorios'] });
  };

  const invalidateAlunos = (id?: string | number) => {
    queryClient.invalidateQueries({ queryKey: ['alunos'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['aluno', queryId]);
    }
  };

  const invalidateEmpresas = (id?: string | number) => {
    invalidateByKey(['empresas']);
    invalidateByKey(['dashboard-stats']);
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['empresa', queryId]);
    }
  };

  const invalidateCidades = (id?: string | number) => {
    invalidateByKey(['cidades']);
    invalidateByKey(['dashboard-stats']);
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['cidade', queryId]);
    }
  };

  const invalidateCursos = (id?: string | number) => {
    invalidateByKey(['cursos']);
    invalidateByKey(['dashboard-stats']);
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['curso', queryId]);
    }
  };

  const invalidateInstituicoes = (id?: string | number) => {
    invalidateByKey(['instituicoes']);
    invalidateByKey(['dashboard-stats']);
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['instituicao', queryId]);
    }
  };

  const invalidateSeguradoras = (id?: string | number) => {
    invalidateByKey(['seguradoras']);
    invalidateByKey(['dashboard-stats']);
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['seguradora', queryId]);
    }
  };

  const invalidateAgentesIntegradores = (id?: string | number) => {
    invalidateByKey(['agentes-integradores']);
    invalidateByKey(['dashboard-stats']);
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['agente-integrador', queryId]);
    }
  };

  const invalidateEstagios = (id?: string | number) => {
    invalidateByKey(['estagios']);
    invalidateByKey(['dashboard-stats']);
    invalidateByKey(['estagios-recentes']);
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['estagio', queryId]);
    }
  };

  const invalidateRelatorios = (id?: string | number) => {
    invalidateByKey(['relatorios']);
    invalidateByKey(['relatorios-proximos-vencer']);
    invalidateByKey(['dashboard-stats']);
    const queryId = normalizeId(id);
    if (queryId) {
      invalidateByKey(['relatorio', queryId]);
    }
  };

  const invalidateHorarios = (params?: {
    estagioId?: string | number;
    horarioId?: string | number;
  }) => {
    invalidateByKey(['horarios']);
    invalidateByKey(['estagios']);

    const normalizedEstagioId = normalizeId(params?.estagioId);
    if (normalizedEstagioId) {
      invalidateByKey(['horarios-estagio', normalizedEstagioId]);
      invalidateByKey(['estagio', normalizedEstagioId]);
    }

    const normalizedHorarioId = normalizeId(params?.horarioId);
    if (normalizedHorarioId) {
      invalidateByKey(['horario', normalizedHorarioId]);
    }
  };

  return {
    invalidateAll,
    invalidateAlunos,
    invalidateEmpresas,
    invalidateCidades,
    invalidateCursos,
    invalidateInstituicoes,
    invalidateSeguradoras,
    invalidateAgentesIntegradores,
    invalidateEstagios,
    invalidateRelatorios,
    invalidateHorarios,
  };
};
